import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { EXPERIMENT_FLAGS, type OnboardingVariant } from '@scio3/shared-types';
import { SET_EXPERIMENT_VARIANT, type SetExperimentVariantResponse } from '@scio3/graphql-client';
import { isPosthogEnabled, posthog } from '@/lib/posthog';
import { useAuthStore } from '@/stores/auth-store';
import { readQaOverride } from '../utils/qa-override';

/**
 * Safety net for the pathological "SDK loaded but never answers" case. Kept
 * generous so a slow-but-working flag load is never pre-empted by a premature
 * control answer (which would flash the survey over the screens for treatment).
 */
const FLAG_SAFETY_TIMEOUT_MS = 8000;

const FLAG = EXPERIMENT_FLAGS.SCIO3B_SPLIT;

function normalizeVariant(value: unknown): OnboardingVariant | null {
  return value === 'scio3a' || value === 'scio3b' ? value : null;
}

/**
 * Resolve the user's variant for the scio3b-split A/B test — backend-first.
 *
 * Order of precedence: QA override → the value stored on the company → the first
 * IDENTIFIED PostHog answer (which is then persisted to the company).
 *
 * - **Company has it** → return it synchronously from the auth-store (this is what
 *   makes the assignment sticky AND makes every consumer agree — they all read one
 *   field, so the nav and the checklist can never disagree, and it never flips).
 * - **Company doesn't have it (first time ever)** → wait for an IDENTIFIED PostHog
 *   answer (`_isIdentified()` and `distinct_id === userId`), then call
 *   `setExperimentVariant` (write-once on the backend) AND optimistically patch the
 *   company in-store so the rest of this session is consistent too.
 * - No SDK / 8s timeout → control (`scio3a`), NOT persisted (so a slow PostHog load
 *   never locks a user into the wrong bucket — next login re-resolves).
 *
 * Resolution lives here (not in AuthProvider) so it runs during the signup flow
 * too — AuthProvider's `loadUserContext` early-returns while `signupInProgress`.
 */
export function useOnboardingVariant(): {
  variant: OnboardingVariant;
  isLoading: boolean;
} {
  // Read override once on mount — subsequent ?force_variant= changes only take
  // effect on full page reload, matching how PostHog assigns variants.
  const [override] = useState<OnboardingVariant | null>(() => readQaOverride());
  const company = useAuthStore((s) => s.company);
  const setCompany = useAuthStore((s) => s.setCompany);
  const userId = useAuthStore((s) => s.me?._id);

  // Source of truth: the variant stored on the company. Reactive — once one
  // consumer resolves + persists it (below), every consumer reads it here.
  const storedVariant = normalizeVariant(company?.experiments?.[FLAG]);

  const [setExperimentVariant] = useMutation<SetExperimentVariantResponse>(SET_EXPERIMENT_VARIANT);

  // Session-locked fallback for the very first time, before the backend value
  // exists. Seeded from override / stored value so returning companies never
  // see the loading skeleton.
  const [locked, setLocked] = useState<OnboardingVariant | null>(
    () => override ?? normalizeVariant(useAuthStore.getState().company?.experiments?.[FLAG]),
  );

  useEffect(() => {
    if (override || storedVariant || locked) return; // already resolved

    // No SDK (no key / embedded iframe) → control; don't sit on the skeleton.
    if (!isPosthogEnabled) {
      setLocked('scio3a');
      return;
    }

    let done = false;

    // Persist a resolved variant: write-once on the backend, and optimistically
    // patch the company in-store so every other consumer reads the SAME value
    // immediately (no first-session divergence between nav and checklist).
    const persist = async (value: OnboardingVariant) => {
      const current = useAuthStore.getState().company;
      if (current && !current.experiments?.[FLAG]) {
        setCompany({
          ...current,
          experiments: { ...(current.experiments ?? {}), [FLAG]: value },
        });
      }
      try {
        await setExperimentVariant({ variables: { key: FLAG, variant: value } });
      } catch {
        // Best-effort — the optimistic store value keeps this session consistent;
        // next login re-resolves and re-persists.
      }
    };

    // Accept ONLY an identified answer, so we never persist the anonymous bucket.
    const accept = () => {
      if (done) return;
      if (!posthog._isIdentified?.() || !userId || posthog.get_distinct_id?.() !== userId) return;
      const value: OnboardingVariant =
        posthog.getFeatureFlag(FLAG) === 'scio3b' ? 'scio3b' : 'scio3a';
      done = true;
      setLocked(value);
      void persist(value);
    };

    accept(); // flags may already be loaded + identified
    const unsub = posthog.onFeatureFlags?.((flags, variants, info) => {
      // Diagnostic: visible in DevTools so you can tell whether PostHog answered,
      // what variant it assigned, and whether the fire was identified.
      console.warn(`[experiment] ${FLAG}: PostHog responded.`, {
        assignedVariant: variants?.[FLAG] ?? '(none — control)',
        identified: posthog._isIdentified?.(),
        distinctId: posthog.get_distinct_id?.(),
        expectedUserId: userId,
        totalActiveFlags: flags.length,
        errorsLoading: info?.errorsLoading,
      });
      accept();
    });

    const timeoutId = window.setTimeout(() => {
      if (done) return;
      done = true;
      // Timeout fallback is NOT persisted — a slow PostHog load must never lock a
      // user into control. They re-resolve next login.
      console.error(
        `[experiment] ${FLAG}: no identified PostHog answer within ${FLAG_SAFETY_TIMEOUT_MS}ms — falling back to control (scio3a). ` +
          `Check that VITE_POSTHOG_KEY is set and VITE_POSTHOG_API_HOST is reachable.`,
      );
      setLocked('scio3a');
    }, FLAG_SAFETY_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeoutId);
      unsub?.();
    };
  }, [override, storedVariant, locked, userId, setCompany, setExperimentVariant]);

  if (override) return { variant: override, isLoading: false };
  if (storedVariant) return { variant: storedVariant, isLoading: false };
  if (locked) return { variant: locked, isLoading: false };
  // Still resolving the first identified answer → keep the skeleton.
  return { variant: 'scio3a', isLoading: true };
}

/**
 * Stable boolean check — useful for `if`-style branching outside JSX without
 * also pulling `isLoading`. During the loading window, returns `false` so
 * callers don't accidentally render scio3b UI before the variant resolves.
 */
export function useIsScio3bVariant(): boolean {
  const { variant, isLoading } = useOnboardingVariant();
  return !isLoading && variant === 'scio3b';
}

/**
 * One-shot side-effect: registers `onboarding_variant` as a PostHog person
 * property once the variant resolves, so every subsequent event auto-attaches
 * it without callers needing to thread the prop through.
 *
 * Mount once near the variant decision point (e.g. inside `OnboardingRouter`).
 */
export function useRegisterVariantProperty(variant: OnboardingVariant, isLoading: boolean): void {
  useEffect(() => {
    if (isLoading) return;
    // Lazy-import the singleton so we don't pull posthog into modules that only
    // need the variant value (e.g. tests).
    import('@/lib/posthog')
      .then(({ posthog }) => {
        posthog.register({ onboarding_variant: variant });
      })
      .catch(() => {
        // Posthog optional — swallow.
      });
  }, [variant, isLoading]);
}
